import {
  NotFound,
  Tooltip,
  Pagination,
  Input,
  DSInputType,
  Typography,
  Table,
  Select,
  HeadItemType
} from 'components'
import { useEffect, useState, useRef } from 'react'
import { toast } from 'react-toastify'
import { Skeleton } from '@mui/material'
import { useParams } from 'react-router-dom'
import { getOVMaterials } from 'services/manage_ov'
import { OvMaterialItem } from 'services/manage_ov/interface'
import { formatToPTBRNumber } from 'commons/utils/number'
import useDebounce from 'commons/customHooks/useDebounce'
import { useSelector, useDispatch } from 'store/store'
import { getDetailedInfo, setMaterialsTabView } from 'store/OVContext/ovSlice'
import { clearMaterialSelected } from 'store/ProductContext'
import { GetNegDif, getNegDifRowIcon } from 'container/ManageOrders/utils'
import { TypesValidations } from 'constants/enums'

import { setOvInitialTab } from 'store/StructuralContext/structuralSlice'
import { toAllFirstLetterUpper } from 'commons/utils/string'

import * as S from './styled'

interface MaterialSize {
  CodigoTamanho: string
  QuantidadeItensPares: number
}

const MaterialsTab = () => {
  const PAGE_SIZE = 10
  const { ovNumber } = useParams()

  const { ovSelected, materialsTabView } = useSelector(({ ov }) => ov)
  const dispatch = useDispatch()
  const [materials, setMaterials] = useState<OvMaterialItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filterName, setFilterName] = useState('')
  const [searchResults, setSearchResults] = useState<OvMaterialItem[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const clearMaterials = () => {
    setMaterials([])
    setCurrentPage(1)
    setTotalItems(0)
    setTotalPages(0)
  }

  const getMaterials = async (page: number, searchValue?: string, tableView?: string) => {
    setLoading(true)
    const selectedView = tableView || materialsTabView
    try {
      const materialsResponse = await getOVMaterials(
        { Paginas: page - 1, Linhas: PAGE_SIZE },
        `${ovNumber}`,
        selectedView,
        {
          descricaoMaterial: searchValue || '',
          fabricaMaterial: ''
        }
      )

      if (materialsResponse.Materiais?.length) {
        const updatedMaterials: OvMaterialItem[] = materialsResponse.Materiais.map(
          (material: OvMaterialItem) => ({
            ...material,
            statusAsNumber: Number(material.Status.split('-')[0]),
            checked: false
          })
        )
        setMaterials(updatedMaterials)
        setCurrentPage(page)
        setTotalItems(materialsResponse.RowsCount)
        setTotalPages(Math.ceil(materialsResponse.RowsCount / PAGE_SIZE))
      } else {
        clearMaterials()
      }
    } catch (error) {
      toast.error('Erro ao carregar materiais')
    } finally {
      setLoading(false)

      const target = document.querySelector('#materiais')
      let targetPosition = 0

      if (searchValue) {
        if (target) {
          targetPosition = window.scrollY + target.getBoundingClientRect().top - 100
        }

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        })
      }
      inputRef?.current?.focus()
    }
  }

  const debounceChange = useDebounce(getMaterials, 1000)

  const getMaterialSizes = (): HeadItemType[] => {
    const sizes: string[] = []

    if (!loading && materials.length) {
      materials.forEach((item: OvMaterialItem) => {
        if (item.TamanhosMaterial?.length) {
          item.TamanhosMaterial.forEach((size: MaterialSize) => {
            if (!sizes.includes(size.CodigoTamanho)) {
              sizes.push(size.CodigoTamanho)
            }
          })
        }
      })

      sizes.sort()
    }

    return sizes.map((size) => ({
      label: size,
      columnName: size
    }))
  }

  const getMaterialsHead = () => {
    const tableHead: HeadItemType[] = [
      { label: 'Item', columnName: 'Item' },
      { label: 'Status', columnName: 'Status' },
      { label: 'Material', columnName: 'Material' },
      { label: 'Descrição', columnName: 'DescricaoMaterial' },
      { label: 'Cor', columnName: 'Cor' },
      { label: 'Descrição', columnName: 'DescricaoCor' },
      { label: 'Estratégico', columnName: 'Estrategico' },
      { label: 'Qtd.', columnName: 'Qtd' },
      { label: 'Valor (R$)', columnName: 'Valor' },
      { label: '% Desc.', columnName: 'PorcentagemDesc.' },
      { label: '$ Desc.', columnName: 'ValorDesc.' },
      { label: 'Qtd. Caixa', columnName: 'QtdCaixa' },
      { label: 'Texto', columnName: 'Texto' }
    ]

    if (materialsTabView === 'cor') {
      const sizes = getMaterialSizes()
      tableHead.push(...sizes)
    } else {
      tableHead.splice(4, 0, { label: 'Tamanho', columnName: 'Tamanho' })
    }

    return tableHead
  }

  const getMaterialsData = () => {
    const tableData = []

    if (searchResults.length) {
      const materialsData = searchResults.map((item: OvMaterialItem) => {
        const sizesRow = getMaterialSizes().map((size) => {
          const foundSize = item.TamanhosMaterial?.find(
            (itemSize: MaterialSize) => itemSize.CodigoTamanho === size.label
          )

          return foundSize ? foundSize.QuantidadeItensPares : <S.EmptySize>0</S.EmptySize>
        })

        const ovNegDifInfo: GetNegDif = {
          flag: item.FlagNegociacaoDiferenciada,
          isJustified: item.DataJustificativa,
          ovStatus: item.statusAsNumber
        }

        const content = [
          <S.Flex key={`item-${item.CodigoMaterial}-${item.CodigoCor}`}>
            {item.NumeroItem}
            {getNegDifRowIcon([ovNegDifInfo])}
          </S.Flex>,
          item.Status.split('-')[0],
          <Tooltip
            arrow
            placement="top"
            key={`tooltip-${item.CodigoMaterial}-${item.CodigoCor}`}
            title={
              <Typography design="hav-default-03-text">
                Preço Unitário: {formatToPTBRNumber(item.ValorUnitario)}
              </Typography>
            }
          >
            <span>{item.CodigoMaterial}</span>
          </Tooltip>,
          toAllFirstLetterUpper(item.DescricaoMaterial),
          item.CodigoCor,
          toAllFirstLetterUpper(item.DescricaoCor),
          item.MaterialEstrategico === TypesValidations.YES ? (
            <S.StrategyText>Sim</S.StrategyText>
          ) : (
            'Não'
          ),
          item.QuantidadeItem,
          formatToPTBRNumber(item.ValorTotalCor),
          `${item.PocentagemDescoto}%`,
          formatToPTBRNumber(item.ValorDesconto),
          item.QuantidadeCaixa ?? '',
          item.FlagMto === TypesValidations.YES ? 'Sim' : 'Não'
        ]

        if (materialsTabView === 'cor') {
          content.push(...sizesRow)
        } else {
          content.splice(4, 0, item.CodigoTamanho ?? '')
        }

        return {
          id: `${item.NumeroItem}`,
          checked: item.checked,
          content
        }
      })

      tableData.push(...materialsData)
    }

    return tableData
  }

  const getMaterialsSkeleton = () => {
    const tableData = []

    for (let i = 0; i <= 2; i++) {
      tableData.push({
        id: `skeleton-${i}`,
        content: Array(getMaterialsHead().length)
          .fill('')
          .map((_, index) => <Skeleton key={index} width={50} height={20} />)
      })
    }

    return tableData
  }

  const handleNameFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()

    setFilterName(value)
    setCurrentPage(1)
    debounceChange(1, value)
  }

  const renderSummary = () => {
    return (
      <S.SummaryRow>
        <S.SummaryItem>
          <Typography design="hav-default-02-text">Qtd. Total OV</Typography>
          {loading ? (
            <Skeleton key={'qtd-ov'} width={80} height={20} />
          ) : (
            <span>{materials[0].QuantidadeTotalOrdemVenda}</span>
          )}
        </S.SummaryItem>
        <S.SummaryItem>
          <Typography design="hav-default-02-text">Vlr. Referência OV</Typography>
          {loading ? (
            <Skeleton key={'vlr-ov-ref'} width={80} height={20} />
          ) : (
            <span>{formatToPTBRNumber(materials[0].ValorTotalOrdemVenda)}</span>
          )}
        </S.SummaryItem>
        <S.SummaryItem>
          <Typography design="hav-default-02-text">Vlr. Total OV/SAP</Typography>
          {loading ? (
            <Skeleton key={'vlr-ov-sap'} width={80} height={20} />
          ) : (
            <span>{formatToPTBRNumber(materials[0].ValorTotalOrdemVendaSap)}</span>
          )}
        </S.SummaryItem>
        <S.SummaryItem>
          <Typography design="hav-default-02-text">Vlr. Total Desconto/OV</Typography>
          {loading ? (
            <Skeleton key={'vlr-ov-desc'} width={80} height={20} />
          ) : (
            <span>{formatToPTBRNumber(materials[0].ValorTotalDesconto)}</span>
          )}
        </S.SummaryItem>
      </S.SummaryRow>
    )
  }

  useEffect(() => {
    dispatch(setOvInitialTab(0))
    dispatch(clearMaterialSelected())
    getMaterials(1)
    dispatch(getDetailedInfo(ovNumber ?? ''))
    return () => {
      dispatch(setMaterialsTabView('cor'))
    }
  }, [])

  useEffect(() => {
    setSearchResults(materials)
  }, [materials])

  const ovIsPending = +ovSelected.Cabecalho.CodigoStatus === 0

  const handleTableViewChange = (value: string) => {
    dispatch(setMaterialsTabView(value))
    clearMaterials()
    setFilterName('')
    getMaterials(1, '', value)
  }

  const tableViewOptions = [
    { value: 'cor', title: 'Cor' },
    { value: 'sku', title: 'SKU' }
  ]

  return (
    <S.Wrapper>
      <S.WrapperHeader isPending={ovIsPending}>
        <S.HeaderActions>
          <S.HeaderActionsField width="78px">
            <Select
              name="TableView"
              value={materialsTabView}
              options={tableViewOptions}
              onChange={(e) => {
                const selectedOption = e.target.value
                handleTableViewChange(selectedOption as string)
              }}
              disabled={loading}
              isMenu
            />
          </S.HeaderActionsField>
          <S.HeaderActionsField width="230px">
            <Input
              addorment
              design={DSInputType.SEARCH_INPUT}
              value={filterName}
              onChange={handleNameFilter}
              InputProps={{ placeholder: 'Buscar material na OV' }}
              disabled={loading}
              ref={inputRef}
            />
          </S.HeaderActionsField>
        </S.HeaderActions>
      </S.WrapperHeader>

      {loading && (
        <Table
          rowHeight={32}
          distributeWidth={false}
          headData={getMaterialsHead()}
          bodyData={getMaterialsSkeleton()}
        />
      )}

      {!loading && materials.length > 0 && (
        <>
          <S.TableWrapper>
            <Table
              rowHeight={32}
              customAlign={[{ colIndex: 1, textAlign: 'center' }]}
              headData={getMaterialsHead()}
              bodyData={getMaterialsData()}
            />
          </S.TableWrapper>
          <S.SummaryWrapper>
            <div>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={(value: number) => getMaterials(value, filterName)}
                  numberOfPages={totalPages}
                  numberOfItems={totalItems}
                  itemsPerPage={PAGE_SIZE}
                />
              )}
            </div>
            {renderSummary()}
          </S.SummaryWrapper>
        </>
      )}

      {!loading && materials.length === 0 && (
        <NotFound
          title="Nenhum material encontrado"
          description="Os materiais relacionados à Ordem de Venda serão listados aqui."
        />
      )}
    </S.Wrapper>
  )
}

export default MaterialsTab
